/**
 * Uma tarefa assíncrona de cada vez — e só a mais recente.
 *
 * Existe por causa de um defeito concreto: `Board.syncUnits` desenha as
 * unidades e, no meio do caminho, ESPERA a textura de cada uma carregar. Duas
 * chamadas disparadas em sequência — o que o React faz o tempo todo, porque
 * estado e apresentação mudam em renderizações diferentes — atravessavam esse
 * ponto de espera juntas, as duas viam o tabuleiro vazio, e as duas criavam um
 * boneco para a mesma unidade. O segundo tomava o lugar no índice; o primeiro
 * ficava órfão na tela, parado na casa de partida para sempre. Era isso que se
 * via ao pedir nova tentativa depois de perder um capítulo: o esquadrão em
 * dobro.
 *
 * A saída não é só enfileirar. Enfileirar conserta a duplicata mas ainda deixa
 * a chamada VELHA terminar depois da nova, e aí o tabuleiro volta a mostrar o
 * estado anterior. Então a fila também descarta o que já foi superado: quando
 * a vez de uma tarefa chega e outra já foi pedida, ela nem começa.
 */
export class LatestOnly {
  #chain: Promise<unknown> = Promise.resolve();
  #ticket = 0;

  /**
   * Enfileira `task`, que roda sozinha e só se ainda for a última pedida.
   *
   * A tarefa recebe `isCurrent`: um teste para consultar depois de cada espera
   * interna, e desistir na hora em que deixar de ser a vez dela. Sem ele, a
   * tarefa superada no meio do caminho seguiria escrevendo por cima do que a
   * seguinte já desenhou.
   */
  run(task: (isCurrent: () => boolean) => Promise<void>): Promise<void> {
    const ticket = ++this.#ticket;
    const mine = (): boolean => ticket === this.#ticket;
    const next = this.#chain.then(() => (mine() ? task(mine) : undefined));
    // O elo da corrente engole o erro: uma sincronia que falhou não pode
    // travar todas as seguintes.
    this.#chain = next.catch(() => undefined);
    return next;
  }

  /**
   * Descarta o que está na fila e desiste do que está no meio do caminho.
   *
   * Para quem limpa o tabuleiro por fora: tudo o que estava sendo desenhado
   * era sobre um estado que não existe mais.
   */
  cancel(): void {
    this.#ticket++;
  }
}
